import React, { useEffect, useRef, useState } from 'react';
import AdminLayout from './AdminLayout';
import { api } from '../../lib/api';
import { Camera, Save, KeyRound, Loader2, UserCog } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../lib/auth';
import { TimezoneSelect } from '../../components/TimezoneSelect';

const SUGGESTED_LANGS = ['English', 'French', 'Spanish', 'Vietnamese', 'Mandarin', 'Portuguese'];

export default function SpecialistSettingsPage() {
  const { setUser } = useAuth();
  const [me, setMe] = useState(null);
  const [allLangs, setAllLangs] = useState(SUGGESTED_LANGS);
  const [pw, setPw] = useState({ current_password: '', new_password: '' });
  const [busy, setBusy] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [picBusting, setPicBusting] = useState(0);
  const fileRef = useRef();

  const load = async () => {
    try {
      const [m, langs] = await Promise.all([api.mySpecialistProfile(), api.myLanguages()]);
      setMe(m);
      setAllLangs(Array.from(new Set([...SUGGESTED_LANGS, ...(langs || [])])));
    } catch { toast.error('Could not load profile'); }
  };
  useEffect(() => { load(); }, []);

  const onField = (k, v) => setMe((p) => ({ ...p, [k]: v }));

  const toggleLang = (l) => {
    const set = new Set(me.languages || []);
    if (set.has(l)) set.delete(l); else set.add(l);
    onField('languages', Array.from(set));
  };

  const save = async () => {
    try {
      setBusy(true);
      const updated = await api.updateMySpecialistProfile({
        first_name: me.first_name, last_name: me.last_name,
        phone: me.phone, title: me.title, bio: me.bio, timezone: me.timezone,
        languages: me.languages, office_location: me.office_location,
        certifications: me.certifications,
      });
      setMe(updated);
      setUser?.(updated);
      toast.success('Profile updated');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const upload = async (f) => {
    if (!f) return;
    const fd = new FormData(); fd.append('file', f);
    try {
      const r = await api.uploadMyPicture(fd);
      const updated = { ...me, profile_picture_url: r.profile_picture_url };
      setMe(updated);
      setUser?.(updated);
      setPicBusting((n) => n + 1);
      toast.success('Picture updated');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
  };

  const changePassword = async () => {
    if (!pw.current_password || pw.new_password.length < 8) {
      return toast.error('Enter current password and a new password of 8+ chars');
    }
    try {
      setPwBusy(true);
      await api.changeMyPassword(pw);
      setPw({ current_password: '', new_password: '' });
      toast.success('Password changed');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not change password'); }
    finally { setPwBusy(false); }
  };

  if (!me) return <AdminLayout title="My Profile"><div className="py-20 text-center text-neutral-500">Loading…</div></AdminLayout>;

  return (
    <AdminLayout title="My Profile" subtitle="Update your details, photo, languages and password.">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-neutral-200 p-6">
            <div className="flex items-center gap-5 mb-6">
              {me.profile_picture_url
                ? <img src={`${process.env.REACT_APP_BACKEND_URL}${me.profile_picture_url}&b=${picBusting}`} alt={me.name} className="w-20 h-20 rounded-full object-cover border-2 border-[#f5b300]" />
                : <div className="w-20 h-20 rounded-full bg-[#f5b300]/15 text-[#c89200] font-bold text-[26px] flex items-center justify-center">{(me.name || 'S').slice(0, 1)}</div>}
              <div>
                <p className="text-[18px] font-bold text-[#14110f]">{me.name}</p>
                <p className="text-[12.5px] text-neutral-500">{me.title || 'Tax Specialist'} · {me.email}</p>
                <button onClick={() => fileRef.current?.click()} data-testid="settings-upload-pic"
                        className="mt-2 inline-flex items-center gap-1.5 text-[12.5px] text-[#c89200] hover:underline font-semibold">
                  <Camera className="w-3.5 h-3.5" /> Change picture
                </button>
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="First name" value={me.first_name} onChange={(v) => onField('first_name', v)} testid="settings-first" />
              <Field label="Last name" value={me.last_name} onChange={(v) => onField('last_name', v)} testid="settings-last" />
              <Field label="Phone" value={me.phone} onChange={(v) => onField('phone', v)} />
              <Field label="Title" value={me.title} onChange={(v) => onField('title', v)} />
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-neutral-500">Timezone</span>
                <div className="mt-1">
                  <TimezoneSelect value={me.timezone} onChange={(v) => onField('timezone', v)}
                                  dataTestid="settings-timezone" />
                </div>
              </label>
              <Field label="Office location" value={me.office_location} onChange={(v) => onField('office_location', v)} />
            </div>
            <label className="block mt-3">
              <span className="text-[11px] uppercase tracking-wider text-neutral-500">Bio</span>
              <textarea value={me.bio || ''} onChange={(e) => onField('bio', e.target.value)} rows={4}
                        className="mt-1 w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none" />
            </label>
            <label className="block mt-3">
              <span className="text-[11px] uppercase tracking-wider text-neutral-500">Certifications</span>
              <input value={me.certifications || ''} onChange={(e) => onField('certifications', e.target.value)}
                     className="mt-1 w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none" />
            </label>

            <div className="mt-4">
              <p className="text-[11px] uppercase tracking-wider text-neutral-500 mb-2">Languages spoken</p>
              <div className="flex flex-wrap gap-2">
                {Array.from(new Set([...allLangs, ...(me.languages || [])])).map((l) => {
                  const on = (me.languages || []).includes(l);
                  return (
                    <button key={l} onClick={() => toggleLang(l)} type="button"
                            className={`px-3 py-1.5 rounded-full text-[12.5px] font-semibold border ${
                              on ? 'bg-[#f5b300] border-[#f5b300] text-[#14110f]' : 'bg-white border-neutral-200 text-neutral-700 hover:border-[#f5b300]/60'
                            }`}>
                      {l}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button onClick={save} disabled={busy} data-testid="settings-save"
                      className="inline-flex items-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold px-5 py-2.5 rounded-md text-[13px]">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-4 h-4" />} Save profile
              </button>
            </div>
          </div>
        </div>

        <aside className="space-y-5">
          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <h3 className="text-[14px] font-bold text-[#14110f] mb-3 flex items-center gap-2"><KeyRound className="w-4 h-4 text-[#c89200]" /> Change password</h3>
            <div className="space-y-2">
              <input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
                     placeholder="Current password" className="w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[13px]" data-testid="pw-current" />
              <input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
                     placeholder="New password (8+ chars)" className="w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[13px]" data-testid="pw-new" />
              <button onClick={changePassword} disabled={pwBusy} data-testid="pw-submit"
                      className="w-full inline-flex items-center justify-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white font-semibold px-4 py-2.5 rounded-md text-[12.5px]">
                {pwBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />} Update password
              </button>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <h3 className="text-[14px] font-bold text-[#14110f] mb-2 flex items-center gap-2"><UserCog className="w-4 h-4 text-[#c89200]" /> Workload</h3>
            <p className="text-[28px] font-extrabold text-[#14110f]">{me.assigned_members_count || 0}</p>
            <p className="text-[12px] text-neutral-500">assigned members</p>
            <p className="text-[12px] text-neutral-500 mt-2">{me.pending_review_count || 0} documents pending your review</p>
          </div>
        </aside>
      </div>
    </AdminLayout>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider text-neutral-500">{label}</span>
      <input value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={testid}
             className="mt-1 w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none" />
    </label>
  );
}
